import { stringHelpers } from '@qdabra/forms-shared/helpers';
import {
    isTextNode,
    isWhitespaceTextNode,
} from '@qdabra/forms-shared/xmlHelpers';
import { allChildNodes, attributeValue, isElement } from 'func-xml';
import { compose, defaultTo, includes, reject, toLower } from 'ramda';

const { equalsCaseInsensitive } = stringHelpers;

const REMOVE_TAGS = [
    'script',
    'style',
    'noscript',
    'iframe',
    'canvas',
    'svg',
    'object',
];

const VALUE_TAGS = ['input', 'textarea', 'select'];

const ALWAYS_NEWLINE_TAGS = ['tr', 'br', 'hr', 'li'];
const POSSIBLE_NEWLINE_TAGS = ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

const isDisplayNone = compose(
    includes('display: none'),
    toLower,
    defaultTo(''),
    attributeValue('style'),
);

const isButton = (node, nodeNameLower) =>
    nodeNameLower === 'button' ||
    (nodeNameLower === 'input' &&
        equalsCaseInsensitive(node.getAttribute('type'), 'button'));

const isHiddenInput = (node, nodeNameLower) =>
    nodeNameLower === 'input' &&
    equalsCaseInsensitive(node.getAttribute('type'), 'hidden');

const isAttachment = (node) =>
    equalsCaseInsensitive(
        node.getAttribute('data-fv-xd-xctname'),
        'inlineimage',
    );

const getClassName = attributeValue('class');

const isInsertItem = (node) => getClassName(node) === 'optionalPlaceholder';

const isMenuWidget = (node) => getClassName(node) === 'fv-menu-widget-holder';

// (DOMNode, String, Boolean) -> Boolean
const shouldIgnoreElement = (element, nodeNameLower, includeButtons) =>
    REMOVE_TAGS.includes(nodeNameLower) ||
    isDisplayNone(element) ||
    (isButton(element, nodeNameLower) && !includeButtons) ||
    isHiddenInput(element, nodeNameLower) ||
    isAttachment(element) ||
    isInsertItem(element) ||
    isMenuWidget(element);

const getLowerLocalName = (node) => node.localName.toLowerCase();

// Boolean -> DOMNode -> Boolean
const shouldIgnoreNode = (includeButtons) => (node) => {
    if (isWhitespaceTextNode(node)) {
        return true;
    }

    if (isTextNode(node)) {
        return false;
    }

    if (!isElement(node)) {
        return true;
    }

    const nodeNameLower = getLowerLocalName(node);

    return shouldIgnoreElement(node, nodeNameLower, includeButtons);
};

const shouldAddNewline = (nodeNameLower, isOnlyChild) => {
    if (ALWAYS_NEWLINE_TAGS.includes(nodeNameLower)) {
        return true;
    }

    return POSSIBLE_NEWLINE_TAGS.includes(nodeNameLower) && !isOnlyChild;
};

const isTableCell = (nodeNameLower) =>
    nodeNameLower === 'td' || nodeNameLower === 'th';

const getChildTextValues = (node, includeButtons) => {
    const validChildren = reject(
        shouldIgnoreNode(includeButtons),
        allChildNodes(node),
    );

    const isOnlyChild = validChildren.length === 1;

    return (
        validChildren
            // eslint-disable-next-line no-use-before-define -- getTextValue and getChildTextValues call each other
            .map(getTextValue(includeButtons, isOnlyChild))
            .filter(Boolean)
            .join('')
    );
};

const getTextValue = (includeButtons, isOnlyChild) => (node) => {
    if (isTextNode(node)) {
        return node.textContent;
    }

    const nodeNameLower = getLowerLocalName(node);

    if (VALUE_TAGS.includes(nodeNameLower)) {
        return node.getAttribute('value');
    }

    if (shouldAddNewline(nodeNameLower, isOnlyChild)) {
        return getChildTextValues(node, includeButtons) + '\n';
    }

    if (isTableCell(nodeNameLower)) {
        return getChildTextValues(node, includeButtons) + '\t';
    }

    return getChildTextValues(node, includeButtons);
};

// DOMNode, Boolean -> String
export const getViewText = getChildTextValues;
